import { useCallback, useEffect, useMemo, useState } from "react";
import UserService from "../../../services/UserService";
import { resolveWordWheelQuestCoins } from "../../../services/PuzzleCoinsUtil";
import MobileCreditApi from "../lib/mobileCreditApi";
import { purchaseRevenueCatPackage, isNativePurchasesAvailable, getRevenueCatIdentity } from "../lib/revenueCatPurchases";
import {
    WORD_WHEEL_CREDIT_IAP_PRODUCTS,
    WORD_WHEEL_CREDIT_PRODUCT_GROUP,
    WORD_WHEEL_LOW_CREDITS_BALANCE,
    WORD_WHEEL_LOW_HINT_POINTS_BALANCE,
    WORD_WHEEL_POINTS_IAP_PRODUCTS,
    WORD_WHEEL_POINTS_PRODUCT_GROUP,
    WORD_WHEEL_QUEST_APP_CODE,
    extractWordWheelProductGroup,
    mergeWordWheelProducts,
} from "../wordWheelQuestCommerce";

export default function useWordWheelWallet() {
    const [creditBalance, setCreditBalance] = useState(0);
    const [lifetimePoints, setLifetimePoints] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [purchaseError, setPurchaseError] = useState("");
    const [purchasingProductId, setPurchasingProductId] = useState("");
    const [creditProducts, setCreditProducts] = useState(WORD_WHEEL_CREDIT_IAP_PRODUCTS);
    const [pointProducts, setPointProducts] = useState(WORD_WHEEL_POINTS_IAP_PRODUCTS);

    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }
        setError("");
        try {
            const [balanceResult, catalog, userInfo] = await Promise.all([
                MobileCreditApi.fetchBalance(WORD_WHEEL_QUEST_APP_CODE).catch(() => ({
                    appCode: WORD_WHEEL_QUEST_APP_CODE,
                    creditBalance: 0,
                })),
                MobileCreditApi.fetchProducts().catch(() => []),
                UserService.getUserInfo().catch(() => null),
            ]);

            setCreditBalance(balanceResult.creditBalance);
            setLifetimePoints(resolveWordWheelQuestCoins(userInfo?.cloudUser));
            setCreditProducts(
                mergeWordWheelProducts(
                    extractWordWheelProductGroup(catalog, WORD_WHEEL_CREDIT_PRODUCT_GROUP),
                    WORD_WHEEL_CREDIT_IAP_PRODUCTS
                )
            );
            setPointProducts(
                mergeWordWheelProducts(
                    extractWordWheelProductGroup(catalog, WORD_WHEEL_POINTS_PRODUCT_GROUP),
                    WORD_WHEEL_POINTS_IAP_PRODUCTS
                )
            );
        } catch (err) {
            setError(err?.message || "Failed to load wallet");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const purchaseProduct = useCallback(async (product, kind) => {
        setPurchaseError("");
        setPurchasingProductId(product.productId);
        try {
            const purchase = await purchaseRevenueCatPackage({
                packageKey: product.packageKey,
                productId: product.productId,
            });
            const rcIdentity = await getRevenueCatIdentity();
            const verified = await MobileCreditApi.verifyIapPurchase({
                appCode: WORD_WHEEL_QUEST_APP_CODE,
                productId: purchase.productId,
                transactionId: purchase.transactionId,
                rawPayload: {
                    platform: "apple",
                    storeProductId: purchase.productId,
                    packageKey: purchase.packageKey,
                    kind,
                    ...rcIdentity,
                    ...(purchase.rawPayload || {}),
                },
            });
            setCreditBalance(verified.creditBalance);
            return verified;
        } catch (err) {
            const message = err?.message || "Purchase failed";
            if (!/cancel/i.test(message)) {
                setPurchaseError(message);
            }
            throw err;
        } finally {
            setPurchasingProductId("");
        }
    }, []);

    const purchaseCreditPack = useCallback(
        (product) => purchaseProduct(product, "credits"),
        [purchaseProduct]
    );

    const purchasePointPack = useCallback(
        (product) => purchaseProduct(product, "points"),
        [purchaseProduct]
    );

    const consumeHintCredits = useCallback(async ({ playId, creditsConsumed }) => {
        const featureUsed = playId
            ? `word_wheel_hint:${playId}:${Date.now()}`
            : `word_wheel_hint:${Date.now()}`;
        const result = await MobileCreditApi.consumeCredits({
            appCode: WORD_WHEEL_QUEST_APP_CODE,
            featureUsed,
            creditsConsumed,
        });
        setCreditBalance(result.creditBalance);
        return result;
    }, []);

    const showCreditPurchase = creditBalance < WORD_WHEEL_LOW_CREDITS_BALANCE;
    const showPointPurchase = creditBalance < WORD_WHEEL_LOW_HINT_POINTS_BALANCE;

    return useMemo(
        () => ({
            creditBalance,
            lifetimePoints,
            creditProducts,
            pointProducts,
            loading,
            refreshing,
            error,
            purchaseError,
            purchasingProductId,
            showCreditPurchase,
            showPointPurchase,
            nativePurchasesAvailable: isNativePurchasesAvailable(),
            refresh,
            purchaseCreditPack,
            purchasePointPack,
            consumeHintCredits,
        }),
        [
            creditBalance,
            lifetimePoints,
            creditProducts,
            pointProducts,
            loading,
            refreshing,
            error,
            purchaseError,
            purchasingProductId,
            showCreditPurchase,
            showPointPurchase,
            refresh,
            purchaseCreditPack,
            purchasePointPack,
            consumeHintCredits,
        ]
    );
}
